import { create } from 'zustand';

import { submitPhq as apiSubmitPhq, type W2PhqPayload } from '../api/w2';

/**
 * W2-mode is the single signal that the reused, branded B2C profile + PHQ screens
 * are being driven through the **forked** W2 employee path (ADR-0010 revised /
 * ADR-0011) rather than the live paying B2C flow.
 *
 * It is switched on only when the tokenized employer link (`/e/<token>`) is open
 * — token presence, NOT the group host, is what makes someone a W2 employee — and
 * it carries the employer token so the deep B2C questionnaire step
 * (`HealthQuestionForm`) can fork persistence to `POST /employee/phq`
 * (`employees.phqPayload`, jsonb) instead of running estimation / writing
 * `applications`.
 *
 * The three B2C files that must diverge (step list, per-step application writes,
 * the PHQ final submit) read `useW2Mode` and branch on `active`; when it is off
 * the live paying flow is byte-identical.
 */

export type W2SubmitState = 'idle' | 'submitting' | 'done' | 'error';

interface W2ModeState {
  /** True while the tokenized W2 employee flow owns the reused B2C screens. */
  active: boolean;
  /** Employer link token (scopes the forked enroll / PHQ writes). */
  token: string | null;
  employerName?: string;

  /** Terminal-submit lifecycle for the questionnaire (drives the done screen). */
  submitState: W2SubmitState;
  submitError: string | null;
  /**
   * From the submit response (phqland-s4 §3.2): the backend says this employee
   * may proceed straight to plan selection. Tolerant decode — only a literal
   * `true` counts, so an older backend (field absent) keeps today's terminal.
   */
  selectionAvailable: boolean;

  /** Turn W2 mode on for a resolved, Active employer link. */
  activate: (token: string, employerName?: string) => void;
  /** Turn W2 mode off (falling back to the untouched B2C flow). */
  deactivate: () => void;

  /**
   * Fork the questionnaire persistence: write the FULL B2C payload to
   * `employees.phqPayload` via the W2 endpoint and flip to the done state.
   * Called by `HealthQuestionForm` at the final (privacy-disclosure) step when
   * `active` — it never runs estimation / writes `applications` in W2 mode.
   */
  completePhq: (payload: W2PhqPayload) => Promise<void>;
}

function submitErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data && typeof data === 'object') {
    const { message, errors } = data as { message?: unknown; errors?: unknown };
    if (typeof message === 'string') return message;
    if (Array.isArray(errors) && typeof errors[0] === 'string') return errors[0];
  }
  return 'We could not submit your questionnaire. Please try again.';
}

export const useW2Mode = create<W2ModeState>((set, get) => ({
  active: false,
  token: null,
  employerName: undefined,
  submitState: 'idle',
  submitError: null,
  selectionAvailable: false,

  activate: (token, employerName) =>
    set({
      active: true,
      token,
      employerName,
      submitState: 'idle',
      submitError: null,
      selectionAvailable: false,
    }),

  deactivate: () =>
    set({
      active: false,
      token: null,
      employerName: undefined,
      submitState: 'idle',
      submitError: null,
      selectionAvailable: false,
    }),

  completePhq: async (payload) => {
    const { token } = get();
    if (!token) return;
    // Store-level re-entry guard (finding M3): defense-in-depth against a
    // double-submit if the UI's disabled-button guard regresses or a non-UI
    // caller invokes this while a submit is already in flight.
    if (get().submitState === 'submitting') return;
    set({ submitState: 'submitting', submitError: null });
    try {
      const result = await apiSubmitPhq(token, payload);
      // Stash BEFORE flipping submitState: the route effect that reads the flag
      // fires on 'done', and both land in one set() so they can never tear.
      set({
        submitState: 'done',
        selectionAvailable: result?.selectionAvailable === true,
      });
    } catch (err) {
      set({ submitState: 'error', submitError: submitErrorMessage(err) });
      throw err;
    }
  },
}));

/**
 * Store-access convention (finding #5) — pick by where you read the flag:
 *   - In React render / components, read reactively via `useIsW2Mode()` (or
 *     `useW2Mode(selector)`) so the UI re-renders when the flag flips.
 *   - In event handlers, module-level functions, effects, and store code (no
 *     render subscription), read imperatively via `useW2Mode.getState().active`
 *     — or, for B2C application/subscription side effects, `guardB2CSideEffect()`.
 * Do NOT call `getState()` during render (it won't re-subscribe).
 */

/** Reactive selector: are we driving the reused B2C screens in W2 mode? */
export const useIsW2Mode = () => useW2Mode((s) => s.active);

/**
 * The single seam (finding #4) that enforces the invariant "a W2 employee must
 * NEVER write to / read B2C `applications`/subscriptions" (ADR-0011). Every B2C
 * application-store side effect (create / update / draft-bootstrap / the
 * application-state tier read) calls this first and bails when the tokenized W2
 * employee link owns the reused screens, so the invariant lives HERE instead of
 * being re-derived from scattered `isW2` checks at each call site. Returns true
 * when the caller must no-op.
 */
export function guardB2CSideEffect(): boolean {
  return useW2Mode.getState().active;
}
